import { useLocation } from "wouter";
import { Loader2, Sparkles, WifiOff, AlertCircle } from "lucide-react";
import { useComposerDrafts, requestOpenDraft, type ComposerDraft } from "@/lib/listingComposer";

// Tray of in-progress listings, stacked above the nav. Each bar is one listing
// the seller started; tapping opens it in the review form. They stay here until
// submitted, so a maker can shoot several items and fill in details afterward.
const MAX_VISIBLE = 3;

function statusFor(draft: ComposerDraft): { icon: JSX.Element; text: string } {
  switch (draft.status) {
    case "queued_offline":
      return { icon: <WifiOff className="h-4 w-4 shrink-0" />, text: "Waiting for service" };
    case "error":
      return { icon: <AlertCircle className="h-4 w-4 shrink-0" />, text: "Tap to finish by hand" };
    case "ready":
      return {
        icon: <Sparkles className="h-4 w-4 shrink-0" />,
        text: draft.analysis?.name ? `${draft.analysis.name} — ready` : "Ready to review",
      };
    default:
      return { icon: <Loader2 className="h-4 w-4 shrink-0 animate-spin" />, text: "Analyzing…" };
  }
}

export function ListingComposerChip() {
  const drafts = useComposerDrafts();
  const [location, setLocation] = useLocation();

  if (drafts.length === 0) return null;
  // The dialog covers the tray while a listing is open, and on checkout the
  // pay bar owns the bottom of the screen.
  if (location.startsWith("/checkout")) return null;

  const visible = drafts.slice(-MAX_VISIBLE);
  const hidden = drafts.length - visible.length;

  const open = (id: string) => {
    requestOpenDraft(id);
    if (location !== "/seller") setLocation("/seller");
  };

  return (
    // Sits above the map toggle (bottom + 64px, ~42px tall) rather than on top
    // of it, since both are bottom-anchored and the tray grows upward.
    // pointer-events-none on the container, auto on each bar: this is a fixed
    // full-width box floating over the page, so without it the empty space and
    // the gaps between bars swallow every tap on whatever is underneath — which
    // made buttons on the seller dashboard dead while a draft was in the tray.
    <div
      className="pointer-events-none fixed inset-x-0 z-40 mx-auto flex w-[calc(100%-1.5rem)] max-w-md flex-col gap-1.5"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 114px)" }}
    >
      {hidden > 0 && (
        <p className="mx-auto rounded-full bg-background px-2 py-0.5 text-center text-[11px] font-medium text-muted-foreground shadow">
          +{hidden} more listing{hidden === 1 ? "" : "s"} in progress
        </p>
      )}
      {visible.map((draft) => {
        const { icon, text } = statusFor(draft);
        return (
          // Deliberately no discard control here. Removing a listing should
          // mean opening it and looking at it first — an × on the bar invites a
          // thoughtless tap that throws away a photo they already took.
          // Discard lives inside the review form, next to Publish.
          //
          // Solid, not translucent + backdrop-blur: the blur picked up the map
          // toggle's dark shadow sitting behind it and rendered it as a grey
          // smear inside the bar, and it made the text hard to read over page
          // content. Nothing floating over other floating elements should use a
          // backdrop filter in this WebView.
          <button
            key={draft.id}
            type="button"
            onClick={() => open(draft.id)}
            className="pointer-events-auto flex items-center gap-2 rounded-full border border-primary/40 bg-background py-1.5 pl-1.5 pr-4 text-left text-sm font-medium text-primary shadow-lg active:scale-[0.99]"
            data-testid={`listing-draft-${draft.id}`}
          >
            <img
              src={`data:image/jpeg;base64,${draft.imageBase64}`}
              alt=""
              className="h-8 w-8 shrink-0 rounded-full object-cover"
            />
            {icon}
            <span className="min-w-0 flex-1 truncate">{text}</span>
            <span className="shrink-0 text-xs opacity-70">Open</span>
          </button>
        );
      })}
    </div>
  );
}
